<template>
  <div class="utility-menu">
    <!-- Closed: the toggle lives in the global header row (AppHeader.vue) so it
         reserves its own space and no screen renders under it. Open: it moves
         into the drawer's own top-left slot, above the nav, as a plain inline
         row — the drawer's z-index 51 carries it above `.grid-universal`
         (z-index 20) with no fixed-positioning hack. -->
    <portal :to="isOpen ? 'utility-menu-drawer-toggle' : 'app-header-start'">
      <button
        type="button"
        class="utility-menu__toggle"
        :class="{
          'utility-menu__toggle--light': isToggleGlyphLight,
          'utility-menu__toggle--open': isOpen,
        }"
        :aria-label="isOpen ? $t('closeAvatar') : $t('options')"
        aria-haspopup="true"
        :aria-expanded="isOpen ? 'true' : 'false'"
        data-testid="audience-utility-menu-toggle-button"
        @click="toggleMenu"
      >
        <span class="utility-menu__burger" aria-hidden="true">
          <span class="utility-menu__burger-bar" />
          <span class="utility-menu__burger-bar" />
          <span class="utility-menu__burger-bar" />
        </span>
      </button>
    </portal>

    <transition name="utility-menu-fade">
      <button
        v-if="isOpen"
        type="button"
        class="utility-menu__backdrop"
        :aria-label="$t('closeAvatar')"
        data-testid="audience-utility-menu-backdrop-button"
        @click="() => dismissMenu('backdrop')"
      />
    </transition>

    <transition name="utility-menu-slide">
      <div
        v-if="isOpen"
        class="utility-menu__drawer"
        role="dialog"
        aria-modal="true"
      >
        <portal-target name="utility-menu-drawer-toggle" />

        <!-- App navigation — these rows were the top tab bar until BOO-312. -->
        <nav class="utility-menu__links" :aria-label="$t('options')">
          <button
            id="nav-item-tab-slide"
            type="button"
            data-testid="audience-tabs-presentation-tab"
            class="utility-menu__link"
            :class="{ 'utility-menu__link--active': isActiveTab('slide') }"
            :aria-current="isActiveTab('slide') ? 'page' : false"
            @click="switchTab(presentationRouteName)"
          >
            <span class="utility-menu__link-icon utility-menu__link-icon--nav">
              <phosphor-icon name="projector-screen" />
            </span>
            {{ $t('presentation') }}
            <span
              v-if="isConnectionGoodOrUnstable"
              class="utility-menu__connection-dot"
              :class="{
                'utility-menu__connection-dot--good': isConnectionGood,
                'utility-menu__connection-dot--unstable': isConnectionUnstable,
              }"
              data-testid="audience-utility-menu-connection-dot"
            />
            <aha-spinner
              v-else-if="isConnectionReconnecting"
              variant="dark"
              class="utility-menu__connection-spinner"
            />
          </button>

          <button
            v-if="isEnableSlideReview"
            id="nav-item-tab-lookback"
            type="button"
            data-testid="audience-tabs-review-tab"
            class="utility-menu__link"
            :class="{
              'utility-menu__link--active': isActiveTab('lookback'),
              'utility-menu__link--disabled': isTabReviewDisabled,
            }"
            :aria-current="isActiveTab('lookback') ? 'page' : false"
            :disabled="isTabReviewDisabled"
            @click="switchTab('lookback', isTabReviewDisabled)"
          >
            <span class="utility-menu__link-icon utility-menu__link-icon--nav">
              <phosphor-icon name="cards" />
            </span>
            {{ $t('review_slides') }}
          </button>

          <button
            v-if="isShowTabFeedback"
            id="nav-item-tab-feedback"
            type="button"
            data-testid="audience-tabs-feedback-tab"
            class="utility-menu__link"
            :class="{
              'utility-menu__link--active': isActiveTab('feedback'),
              'utility-menu__link--disabled': isTabFeedbackDisabled,
            }"
            :aria-current="isActiveTab('feedback') ? 'page' : false"
            :disabled="isTabFeedbackDisabled"
            @click="switchTab('feedback', isTabFeedbackDisabled)"
          >
            <span class="utility-menu__link-icon utility-menu__link-icon--nav">
              <phosphor-icon name="chat-centered-dots" />
            </span>
            {{ $t('give_feedback') }}
          </button>

          <button
            v-if="isShowTabProfile"
            id="nav-item-tab-profile"
            type="button"
            data-testid="audience-tabs-profile-tab"
            class="utility-menu__link"
            :class="{ 'utility-menu__link--active': isActiveTab('profile') }"
            :aria-current="isActiveTab('profile') ? 'page' : false"
            @click="switchTab(profileRouteName)"
          >
            <span class="utility-menu__link-icon utility-menu__link-icon--nav">
              <phosphor-icon name="user" />
            </span>
            {{ $t('my_account') }}
          </button>

          <button
            v-if="isShowTabZoomLogout"
            type="button"
            data-testid="audience-tabs-logout-button"
            class="utility-menu__link"
            @click="logoutZoomApp"
          >
            <span class="utility-menu__link-icon utility-menu__link-icon--nav">
              <phosphor-icon name="sign-out" />
            </span>
            {{ $t('log_out') }}
          </button>
        </nav>

        <hr v-if="isShowTabProfile" class="utility-menu__separator">

        <div v-if="isShowTabProfile" class="utility-menu__links">
          <button
            type="button"
            class="utility-menu__link"
            data-testid="audience-utility-menu-terms-link"
            @click="viewTerms"
          >
            <phosphor-icon name="file-text" class="utility-menu__link-icon" />
            {{ $t('term') }}
            <phosphor-icon name="arrow-square-out" :size="16" class="utility-menu__link-external" />
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import util from '@/util'
import AppNavMixin from '@/mixins/app-nav.mixin'
import PhosphorIcon from '@/components/icons/PhosphorIcon.vue'
import { CONNECTION_STATUS } from '@/utils/system/SystemConnection'

const TERMS_URL = 'https://ahaslides.com/terms/?source=audience_home&medium=audience_app&campaign=terms'

export default {
  components: { PhosphorIcon },
  mixins: [AppNavMixin],
  data() {
    return {
      isOpen: false,
    }
  },
  computed: {
    ...mapGetters('systemConnection', ['connectionStatus']),
    hasJoined() {
      return Boolean(this.audienceId)
    },
    isConnectionGood() {
      return this.connectionStatus === CONNECTION_STATUS.GOOD
    },
    isConnectionUnstable() {
      return this.connectionStatus === CONNECTION_STATUS.UNSTABLE
    },
    isConnectionGoodOrUnstable() {
      return this.hasJoined && (this.isConnectionGood || this.isConnectionUnstable)
    },
    isConnectionReconnecting() {
      return this.hasJoined && this.connectionStatus === CONNECTION_STATUS.RECONNECTING
    },
    // The toggle overlays the current surface: every tab is the app's own light
    // UI except the slide tab, which paints the deck's background. So the glyph
    // goes light only on the slide tab of a deck that itself uses light text
    // (i.e. a dark slide background); everywhere else the default dark glyph is
    // the legible one.
    isToggleGlyphLight() {
      return this.activeTabView === 'slide'
        && this.$store.getters['themeModule/isTextColourLight']
    },
  },
  watch: {
    isOpen(open) {
      if (open) {
        document.addEventListener('keydown', this.onKeydown)
      } else {
        document.removeEventListener('keydown', this.onKeydown)
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    // The top-left toggle both opens the menu and, once open, morphs into the X
    // that closes it, so the Close event the old drawer button fired now rides
    // the close half of this toggle; Esc and backdrop close fire it via dismissMenu.
    toggleMenu() {
      this.isOpen = !this.isOpen

      util.ga4Track(this.isOpen ? 'Utility menu > Open' : 'Utility menu > Close')
    },
    closeMenu() {
      this.isOpen = false
    },
    dismissMenu(method) {
      util.ga4Track('Utility menu > Close', { method })
      this.closeMenu()
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.dismissMenu('escape')
      }
    },
    // `handleSwitchTab` / `handleLogoutZoomApp` come from AppNavMixin and carry
    // the routing + GA4 behaviour the old tab bar had unchanged; the drawer only
    // adds "close myself afterwards".
    switchTab(routeName, isTabDisabled = false) {
      this.handleSwitchTab(routeName, isTabDisabled)

      if (!isTabDisabled) {
        this.closeMenu()
      }
    },
    logoutZoomApp() {
      this.handleLogoutZoomApp()
      this.closeMenu()
    },
    openHref(url) {
      if (window) {
        const windowsOpen = window.open(url, '_blank')
        if (windowsOpen) {
          windowsOpen.focus()
        }
      }
    },
    viewTerms() {
      this.openHref(TERMS_URL)

      util.ga4Track('Utility menu > View terms')
      this.closeMenu()
    },
  },
}
</script>

<style lang="scss" scoped>
// Every colour below comes from the shared design system (the storybook
// Tailwind theme) rather than a one-off hex — BOO-312 r6. Where alpha is needed
// Tailwind's `theme()` cannot help, so the rgba is written from the same token
// value and named in a comment.
.utility-menu {
  &__toggle {
    position: relative;
    // Sits in AppHeader's start slot; the margin is what puts it 12px from the
    // top-left corner, and it stays there whether or not a logo renders.
    margin: 12px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: theme('colors.base.0');
    transition: background 0.15s ease;
    // Dark glyph reads on the app's light surfaces (every tab but the slide);
    // the `--light` modifier flips it to white for the slide tab of a
    // dark-themed deck, whose surface is dark.
    color: theme('colors.secondary.100');
    line-height: 1;
    cursor: pointer;

    &:hover {
      background: theme('colors.secondary.15');
    }

    &--light {
      color: theme('colors.base.0');
      // theme('colors.secondary.100') at 55% — a translucent navy fill so the
      // white glyph clears 3:1 over light and dark slides, lightening on hover.
      background: rgba(40, 46, 62, 0.55);

      &:hover {
        background: rgba(40, 46, 62, 0.6);
      }
    }

    // Open state: the toggle sits inside the drawer as a plain glyph on the
    // white surface — its burger glyph left-aligned to the nav-icon column, its
    // own row with a small gap above the list. No pill/shadow/blur here, and the
    // dark glyph is forced back so it stays legible even when `--light` was set
    // for a dark slide behind the drawer. Placed after `--light` so it wins the
    // colour and background overrides at equal specificity.
    &--open {
      position: relative;
      // 2px left = the 12px nav-icon inset minus the button's own 10px glyph
      // centring, so the X lines up with the row icons below it.
      margin: 0 0 4px 2px;
      color: theme('colors.secondary.100');
      background: transparent;
      box-shadow: none;
      backdrop-filter: none;

      &:hover {
        background: theme('colors.secondary.15');
      }
    }
  }

  // The three bars morph in place between the hamburger and the X, so the one
  // top-left toggle is also the close control. Bars use currentColor, so the
  // dark/light glyph flip on the toggle above drives them unchanged.
  &__burger {
    position: relative;
    width: 20px;
    height: 16px;
    display: block;
  }

  &__burger-bar {
    position: absolute;
    left: 0;
    width: 100%;
    height: 2px;
    border-radius: 2px;
    background: currentColor;
    transition: transform 0.25s ease, opacity 0.25s ease;

    &:nth-child(1) { top: 0; }
    &:nth-child(2) { top: 7px; }
    &:nth-child(3) { top: 14px; }
  }

  // The open toggle remounts into the drawer with `--open` already applied, so
  // a class transition has no state to interpolate. An enter keyframe replays
  // the three-bars->X morph as the drawer slides in; the static transforms below
  // hold the final X once it finishes.
  &__toggle--open &__burger-bar {
    &:nth-child(1) {
      transform: translateY(7px) rotate(45deg);
      animation: utility-menu-burger-top 0.25s ease;
    }

    &:nth-child(2) {
      opacity: 0;
      animation: utility-menu-burger-mid 0.25s ease;
    }

    &:nth-child(3) {
      transform: translateY(-7px) rotate(-45deg);
      animation: utility-menu-burger-bottom 0.25s ease;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &__burger-bar {
      transition: none;
    }

    &__toggle--open &__burger-bar {
      animation: none;
    }
  }

  &__backdrop {
    position: fixed;
    inset: 0;
    z-index: 50;
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    // theme('colors.secondary.100') at 35%
    background: rgba(40, 46, 62, 0.35);
    cursor: pointer;
  }

  &__drawer {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 51;
    height: 100%;
    width: 260px;
    max-width: 80vw;
    background: theme('colors.base.0');
    // theme('colors.secondary.100') at 18%
    box-shadow: 2px 0 12px rgba(40, 46, 62, 0.18);
    // The opaque drawer covers the header row while open, so the top wants only a small gap, not the full header height.
    padding: 16px 8px 24px;
    box-sizing: border-box;
    overflow-y: auto;
  }

  &__links {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__separator {
    height: 1px;
    margin: 8px 12px;
    border: none;
    background: theme('colors.secondary.20');
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 14px 12px;
    border: none;
    background: transparent;
    text-align: left;
    color: theme('colors.secondary.100');
    font-size: 0.9375rem;
    font-weight: 600;
    line-height: 1.25;
    cursor: pointer;
    border-radius: 8px;

    &:hover {
      background: theme('colors.secondary.15');
    }

    &--active {
      background: theme('colors.primary.20');
      color: theme('colors.primary.90');

      &:hover {
        background: theme('colors.primary.20');
      }

      .utility-menu__link-icon--nav {
        color: theme('colors.primary.90');
      }
    }

    &--disabled {
      color: theme('colors.secondary.40');
      cursor: not-allowed;

      &:hover {
        background: transparent;
      }

      .utility-menu__link-icon--nav {
        color: theme('colors.secondary.40');
      }
    }
  }

  // Every glyph is now a Phosphor "regular" path drawn with `fill:
  // currentColor`, so the icons simply inherit the row's colour — no
  // stroke-drawn exception left to special-case.
  &__link-icon {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    color: theme('colors.secondary.100');

    &--nav {
      position: relative;
    }
  }

  &__connection-dot {
    margin-left: auto;
    flex: none;
    width: 10px;
    height: 10px;
    border-radius: 100%;

    &--good {
      background: #33C173;
    }

    &--unstable {
      background: #FFA826;
    }
  }

  &__connection-spinner {
    margin-left: auto;
    flex: none;
  }

  &__link-external {
    margin-left: auto;
    flex: none;
    color: theme('colors.secondary.50');
  }
}

@keyframes utility-menu-burger-top {
  from { transform: translateY(0) rotate(0); }
  to { transform: translateY(7px) rotate(45deg); }
}

@keyframes utility-menu-burger-mid {
  from { opacity: 1; }
  to { opacity: 0; }
}

@keyframes utility-menu-burger-bottom {
  from { transform: translateY(0) rotate(0); }
  to { transform: translateY(-7px) rotate(-45deg); }
}

.utility-menu-fade-enter-active,
.utility-menu-fade-leave-active {
  transition: opacity 0.2s ease;
}
.utility-menu-fade-enter,
.utility-menu-fade-leave-to {
  opacity: 0;
}

.utility-menu-slide-enter-active,
.utility-menu-slide-leave-active {
  transition: transform 0.25s ease;
}
.utility-menu-slide-enter,
.utility-menu-slide-leave-to {
  transform: translateX(-100%);
}
</style>
